import { Company, ImportRow, ImportRun, ImportVia, ProtoDb } from '../models/domain.models';
import { hashOf } from './commission-math';
import { LINE_PREFIX } from './policy-helpers';
import { addDays, Rng } from './rng';
import { COMPANY, TREE_FEDERAL, TREE_RIOS } from './seed-named';

const INCOMPLETE_MISSING = [
  'Falta el medio de pago de las cuotas', 'Faltan los ítems de riesgo', 'Falta el domicilio del tomador',
  'Faltan las cuotas', 'Falta el PDF de la póliza',
];
const REJECTION_REASONS = [
  'Número de póliza ausente', 'Vigencia inválida: el fin es anterior al inicio', 'DNI o CUIT del tomador inválido',
  'Formato de fila no reconocido', 'Prima en blanco o no numérica',
];

export interface SimulatedImport {
  rows: ImportRow[];
  personsCreated: number;
  personsFound: number;
}

/** M-09: any "uploaded" file or API read yields a simulated outcome, deterministic per `key`. Nothing is imported. */
export function simulateImport(key: string, company: Company): SimulatedImport {
  const rng = new Rng(hashOf(key));
  const total = rng.int(24, 70);
  const rows: ImportRow[] = [];
  for (let i = 1; i <= total; i++) {
    const roll = rng.next();
    const line = rng.pick(company.lines);
    const number = `${LINE_PREFIX[line]}-${String(rng.int(0, 999999)).padStart(6, '0')}`;
    if (roll < 0.62) rows.push({ reference: number, group: 'imported', detail: 'Incorporada a la cartera' });
    else if (roll < 0.8) rows.push({ reference: number, group: 'duplicate', detail: 'Ya existía en la cartera (misma compañía y número)' });
    else if (roll < 0.92) rows.push({ reference: number, group: 'incomplete', detail: rng.pick(INCOMPLETE_MISSING) });
    else rows.push({ reference: rng.chance(0.5) ? number : `Fila ${i}`, group: 'rejected', detail: rng.pick(REJECTION_REASONS) });
  }
  const withPerson = rows.filter(r => r.group === 'imported' || r.group === 'incomplete').length;
  const personsCreated = Math.round(withPerson * 0.35);
  return { rows, personsCreated, personsFound: withPerson - personsCreated };
}

export function importRun(
  oid: string, treeOid: string, memberOid: string, date: string, company: Company, via: ImportVia, fileName: string | null,
): ImportRun {
  const simulated = simulateImport(oid, company);
  return { oid, treeOid, memberOid, date, companyOid: company.oid, via, fileName, ...simulated, undone: false, undoneDate: null };
}

/** Earlier imports shown in each tree's history; derived without the seed RNG. */
export function enrichImports(db: ProtoDb, today: string): void {
  const company = (oid: string) => db.companies.find(c => c.oid === oid)!;
  const rootOf = (treeOid: string) => db.trees.find(t => t.oid === treeOid)?.rootOid ?? '';
  const runs: ImportRun[] = [
    importRun('imp-0001', TREE_RIOS, rootOf(TREE_RIOS), addDays(today, -82), company(COMPANY.austral), 'file', 'cartera_austral_2026-07.csv'),
    importRun('imp-0002', TREE_RIOS, rootOf(TREE_RIOS), addDays(today, -35), company(COMPANY.plata), 'api', null),
    importRun('imp-0003', TREE_FEDERAL, rootOf(TREE_FEDERAL), addDays(today, -20), company(COMPANY.pampa), 'api', null),
  ];
  runs[0].undone = true;
  runs[0].undoneDate = addDays(today, -81);
  db.importRuns = runs;
}
